import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_TAGS } from '../../graphql/queries/tags';
import { CREATE_TAG, UPDATE_TAG, DELETE_TAG } from '../../graphql/mutations/tags';
import { toast } from 'react-hot-toast';

const SEED_TAGS = [
  'needs-review', 'core', 'exit-candidate', 'dust',
  'recovery', 'no-new-capital', 'reduce-on-strength',
  'tax-loss-candidate', 'waiting-for-catalyst'
];

const DEFAULT_COLORS = [
  '#4f46e5', '#059669', '#d97706', '#dc2626', '#7c3aed',
  '#0891b2', '#be185d', '#65a30d', '#ea580c', '#4338ca'
];

const TagManager = () => {
  const { data, loading } = useQuery(GET_TAGS);
  const tags = data?.tags || [];

  const [createTag] = useMutation(CREATE_TAG, { refetchQueries: [{ query: GET_TAGS }] });
  const [updateTag] = useMutation(UPDATE_TAG, { refetchQueries: [{ query: GET_TAGS }] });
  const [deleteTag] = useMutation(DELETE_TAG, { refetchQueries: [{ query: GET_TAGS }] });

  const [showCreate, setShowCreate] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState(DEFAULT_COLORS[0]);
  const [formDescription, setFormDescription] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formName.trim()) return;
    try {
      await createTag({ variables: { input: { name: formName.trim(), color: formColor || null, description: formDescription.trim() || null } } });
      toast.success('Tag created');
      setFormName('');
      setFormColor(DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)]);
      setFormDescription('');
      setShowCreate(false);
    } catch (err) {
      toast.error(err.message || 'Failed to create tag');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingTag) return;
    try {
      await updateTag({ variables: { input: { id: editingTag.id, name: formName.trim() || null, color: formColor || null, description: formDescription.trim() || null } } });
      toast.success('Tag updated');
      setEditingTag(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update tag');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTag({ variables: { id } });
      toast.success('Tag deleted');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete tag');
    }
  };

  const handleSeedCreate = async (name) => {
    const color = DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)];
    try {
      await createTag({ variables: { input: { name, color } } });
      toast.success(`Created "${name}"`);
    } catch (err) {
      toast.error(err.message || 'Failed to create tag');
    }
  };

  const startEdit = (tag) => {
    setEditingTag(tag);
    setFormName(tag.name);
    setFormColor(tag.color || DEFAULT_COLORS[0]);
    setFormDescription(tag.description || '');
    setShowCreate(false);
  };

  const startCreate = () => {
    setEditingTag(null);
    setFormName('');
    setFormColor(DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)]);
    setFormDescription('');
    setShowCreate(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Tags</h2>
        <button
          onClick={startCreate}
          className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          + New Tag
        </button>
      </div>

      {tags.length === 0 && !showCreate && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
          <p className="text-gray-600 dark:text-gray-300 mb-3">
            Tags let you label positions for strategy triage without changing groups or accounting.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Suggested tags:</p>
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {SEED_TAGS.map(name => (
              <button
                key={name}
                onClick={() => handleSeedCreate(name)}
                className="text-xs px-2.5 py-1 rounded-full border border-dashed border-gray-400 dark:border-gray-500 text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-500"
              >
                + {name}
              </button>
            ))}
          </div>
          <button onClick={startCreate} className="text-sm text-blue-600 hover:text-blue-700 underline">
            Or create your own
          </button>
        </div>
      )}

      {(showCreate || editingTag) && (
        <form onSubmit={editingTag ? handleUpdate : handleCreate} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
            {editingTag ? 'Edit Tag' : 'New Tag'}
          </h3>
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Tag name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
              <input
                type="color"
                value={formColor}
                onChange={(e) => setFormColor(e.target.value)}
                className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                title="Tag color"
              />
            </div>
            <input
              type="text"
              placeholder="Description (optional)"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="flex gap-2">
              <button type="submit" className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                {editingTag ? 'Save' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => { setShowCreate(false); setEditingTag(null); }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {tags.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow divide-y divide-gray-200 dark:divide-gray-700">
          {tags.map(tag => (
            <div key={tag.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color || '#6b7280' }}
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{tag.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    {tag.positionCount} position{tag.positionCount !== 1 ? 's' : ''}
                    {tag.stockPositionCount > 0 && ` · ${tag.stockPositionCount} stock`}
                  </span>
                  {tag.description && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">{tag.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => startEdit(tag)}
                  className="text-xs text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                >
                  Edit
                </button>
                {confirmDelete === tag.id ? (
                  <span className="flex items-center gap-1">
                    <span className="text-xs text-red-600">{tag.positionCount + (tag.stockPositionCount || 0)} pos affected</span>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(tag.id)}
                    className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagManager;
